import React from "react";
import styled from "styled-components";
import { Row, Col } from "react-bootstrap";
import ResponsiveImage from "src/components/ImageWrapper/ResponsiveImage";
import useResponsiveImageUrl from "src/utils/useResponsiveImageUrl";
import ReactMarkdown from "react-markdown";

/**
 * Promotional banner section with custom styling
 * Used for special announcements and featured content
 */
const TournamentPromoBanner = ({ banner }) => {
  if (!banner) return null;

  // Check if using new image-based layout
  const hasImageLayout = banner.eventImage || banner.dateImage;
  const bgImage = useResponsiveImageUrl(
    "events/wsop-c-playground-spring-2026/WSOP-super-circuit-background.png",
  );
  const bannerBgImage = banner.bgImage
    ? useResponsiveImageUrl(banner.bgImage)
    : null;

  return (
    <BannerContainer
      bgColor={banner.bgColor}
      bgImage={bannerBgImage}
      overlayColor={banner.overlayColor}
      borderColor={banner.borderColor}
    >
      <BannerInner>
        {hasImageLayout ? (
          <Row className="align-items-start justify-content-start">
            <Col md={3} className="text-start">
              {banner.image && (
                <LogoImage>
                  <ResponsiveImage
                    src={banner.image}
                    alt={banner.imageAlt || "Promotional Banner"}
                  />
                </LogoImage>
              )}
            </Col>
            <Col md={7} className="text-center">
              <ImageStack>
                {banner.eventImage && (
                  <StackedImage>
                    <ResponsiveImage src={banner.eventImage} alt="Event Name" />
                  </StackedImage>
                )}
                {banner.dateImage && (
                  <StackedImage>
                    <ResponsiveImage src={banner.dateImage} alt="Event Dates" />
                  </StackedImage>
                )}
              </ImageStack>
            </Col>
            <Col md={3} className="text-center">
            </Col>
          </Row>
        ) : (
          <>
            {banner.image && (
              <BannerImage>
                <ResponsiveImage
                  src={banner.image}
                  alt={banner.imageAlt || "Promotional Banner"}
                />
              </BannerImage>
            )}
            {banner.title && (
              <BannerTitle color={banner.titleColor}>
                <ReactMarkdown>{banner.title}</ReactMarkdown>
              </BannerTitle>
            )}
          </>
        )}

        {banner.content && (
          <BannerContent textAlign={banner.textAlign} bgImage={bgImage}>
            <ReactMarkdown>{banner.content}</ReactMarkdown>
          </BannerContent>
        )}
      </BannerInner>
    </BannerContainer>
  );
};

const BannerContainer = styled.div`
background-color: var(--black);
  ${(props) =>
    props.bgImage &&
    `
    background-image: url(${props.bgImage});
    background-size: cover;
    background-position: top;
    background-repeat: no-repeat;
  `}
  width: 100vw;
  max-width: 100vw;
  margin: 0 calc(50% - 50vw);
  border-radius: 0;
  padding: 3rem 0;
  /* border: ${(props) =>
    props.borderColor ? `3px solid ${props.borderColor}` : "3px solid #CC0000"}; */
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.7);
  position: relative;
  overflow: hidden;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: var(--black);
    opacity: 0.8;
    z-index: 1;
  }

  > * {
    position: relative;
    z-index: 2;
  }

  @media (max-width: 768px) {
    padding: 2rem 0;
    margin: 1.5rem calc(50% - 50vw);
  }
`;

const BannerInner = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0;
   @media (max-width: 768px) {
    padding: 0 1rem;
   }
`;

const BannerImage = styled.div`
  width: 100%;
  text-align: center;

  img {
    max-width: 100%;
    height: auto;
    max-height: 180px;
    object-fit: contain;
  }

  @media (max-width: 768px) {
    img {
      max-height: 120px;
    }
  }
`;

const LogoImage = styled.div`
  img {
    max-width: 100%;
    height: auto;
    max-height: 200px;
    object-fit: contain;
  }

  @media (max-width: 768px) {
    margin-bottom: 2rem;

    img {
      max-height: 150px;
    }
  }
`;

const ImageStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const StackedImage = styled.div`
  width: 100%;
  text-align: center;

  img {
    max-width: 100%;
    height: auto;
    max-height: 140px;
    object-fit: contain;
  }

  @media (max-width: 768px) {
    img {
      max-height: 80px;
    }
  }
`;

const BannerTitle = styled.div`
  color: ${(props) => props.color || "var(--white)"};
  font-weight: 700;
  text-align: center;

  h1 {
    color: inherit;
    margin: 0 0 1rem 0;
    font-weight: 700;

    em {
      font-style: italic;
    }
  }

  h2 {
    color: var(--white);
    margin: 0.5rem 0;
    font-weight: 600;

    strong {
      color: var(--red);
    }
  }

  h3 {
    color: inherit;
    margin: 0 0 1rem 0;
    font-weight: 700;
  }

  h4 {
    color: #cccccc;
    margin: 0.5rem 0;
    font-weight: 600;
  }

  h5 {
    color: #cccccc;
    margin: 0.5rem 0;
    font-weight: 500;
    background: rgba(255, 255, 255, 0.1);
    padding: 0.5rem 1rem;
    border-radius: 5px;
    display: inline-block;
  }

  @media (max-width: 768px) {
  }
`;

const BannerContent = styled.div`
  line-height: 1.8;
  text-align: ${(props) => props.textAlign || "center"};
  color: var(--white);
  padding: 0 2rem;
  border-radius: 10px;
  position: relative;
  margin-top: 2rem;

  h3 {
    font-size: 2.2rem;
    margin-bottom: 1.5rem;
    font-weight: 700;
    color: var(--white);
  }

  p {
    font-size: 1.5rem;
    line-height: 1.7;
  }

  strong {
    color: var(--white);
    font-weight: 700;
    font-style: italic;
  }

  blockquote {
    background: rgba(60, 60, 60, 0.9);
    padding: 2rem;
    border-left: none;
    line-height: 1.7;

    p {
      margin: 0;
      color: var(--white);
    }
  }

  @media (max-width: 768px) {
    padding: 0;

    p {
      font-size: inherit;
      line-height: inherit;
    }

    blockquote {
      padding: 1rem;

      p {
        font-size: inherit;
        line-height: inherit;
      }
    }
  }
`;

export default TournamentPromoBanner;
